import { useState } from 'react'
import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import DocumentViewerModal from '../auth/DocumentViewerModal'
import { documentPdfUrl } from '../../lib/documentPdfUrl'

export interface MarketingConsentState {
  email_marketing: boolean
  phone_marketing: boolean
}

interface MarketingConsentProps {
  value: MarketingConsentState
  onChange: (next: MarketingConsentState) => void
  disabled?: boolean
  variant?: 'light' | 'dark'
}

const CONSENT_FIELDS: { key: keyof MarketingConsentState; labelKey: string }[] = [
  { key: 'email_marketing', labelKey: 'auth:marketingConsent.email.label' },
  { key: 'phone_marketing', labelKey: 'auth:marketingConsent.phone.label' },
]

export default function MarketingConsent({
  value,
  onChange,
  disabled = false,
  variant = 'light',
}: MarketingConsentProps) {
  const isDark = variant === 'dark'
  const [viewerOpen, setViewerOpen] = useState(false)
  const { t, i18n } = useTranslation(['auth'])

  const documentUrl = documentPdfUrl('zgody_marketingowe', i18n.language)

  const handleChange = (key: keyof MarketingConsentState, checked: boolean) => {
    onChange({ ...value, [key]: checked })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {t('auth:marketingConsent.heading')}
        </p>
        <button
          type="button"
          onClick={() => setViewerOpen(true)}
          disabled={disabled}
          className={`text-xs hover:underline inline-flex items-center gap-1 disabled:opacity-50 ${
            isDark ? 'text-gray-400 hover:text-white' : 'text-gray-700 hover:text-[#212121]'
          }`}
        >
          <FileText className="h-3.5 w-3.5" />
          {t('auth:marketingConsent.documentLink')}
        </button>
      </div>

      <p className="text-xs text-gray-500">
        {t('auth:marketingConsent.intro')}
      </p>

      {CONSENT_FIELDS.map(({ key, labelKey }) => (
        <div key={key} className="flex items-start space-x-3">
          <input
            type="checkbox"
            id={`marketing-${key}`}
            checked={value[key]}
            onChange={(e) => handleChange(key, e.target.checked)}
            disabled={disabled}
            className={`mt-0.5 h-4 w-4 rounded ${
              isDark
                ? 'text-[#FF5A00] focus:ring-[#FF5A00] border-gray-500 bg-[#1a1a1a]'
                : 'text-[#212121] focus:ring-[#212121] border-gray-300'
            }`}
          />
          <label
            htmlFor={`marketing-${key}`}
            className={`text-sm cursor-pointer leading-snug ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
          >
            {t(labelKey)}
          </label>
        </div>
      ))}

      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        documentUrl={documentUrl}
        documentName={t('auth:marketingConsent.documentLink')}
      />
    </div>
  )
}
